import React from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const TrendChart = ({ 
  title, 
  data, 
  dataKey,
  previousYearData,
  planData,
  showPlan = false,
  type = 'line',
  color = 'var(--color-primary)',
  height = 200,
  onClick 
}) => {
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-md p-3 shadow-lg">
          <p className="text-xs font-caption text-muted-foreground mb-1">
            {payload?.[0]?.payload?.name}
          </p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm font-medium font-data" style={{ color: entry?.color }}>
              {entry?.name}: {entry?.value?.toLocaleString('en-US')}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div 
      className={`bg-card rounded-lg p-4 md:p-6 shadow-sm border border-border transition-smooth hover-lift ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <h4 className="text-sm md:text-base font-medium text-foreground mb-4 font-caption">
        {title}
      </h4>
      <div style={{ width: '100%', height: height }} aria-label={`${title} trend chart`}>
        <ResponsiveContainer>
          {type === 'area' ? (
            <AreaChart data={data}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={color} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="name" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="top" 
                height={36}
                iconType="line"
                formatter={(value) => <span className="text-xs text-foreground">{value}</span>}
              />
              <Area 
                type="monotone" 
                dataKey={dataKey} 
                name="Current Year"
                stroke={color}
                strokeWidth={2}
                fill="url(#colorValue)"
              />
              {previousYearData && (
                <Area 
                  type="monotone" 
                  dataKey="previousYear" 
                  name="Previous Year"
                  stroke={color}
                  strokeWidth={1}
                  strokeOpacity={0.4}
                  fill="none"
                />
              )}
              {showPlan && planData && (
                <Line 
                  type="monotone" 
                  dataKey="plan" 
                  name="Plan"
                  stroke={color}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              )}
            </AreaChart>
          ) : (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="name" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="top" 
                height={36}
                iconType="line"
                formatter={(value) => <span className="text-xs text-foreground">{value}</span>}
              />
              <Line 
                type="monotone" 
                dataKey={dataKey} 
                name="Current Year"
                stroke={color}
                strokeWidth={2}
                dot={{ fill: color, r: 4 }}
                activeDot={{ r: 6 }}
              />
              {previousYearData && (
                <Line 
                  type="monotone" 
                  dataKey="previousYear" 
                  name="Previous Year"
                  stroke={color}
                  strokeWidth={1}
                  strokeOpacity={0.4}
                  strokeDasharray="3 3"
                  dot={false}
                />
              )}
              {showPlan && planData && (
                <Line 
                  type="monotone" 
                  dataKey="plan" 
                  name="Plan"
                  stroke={color}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              )}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TrendChart;